import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import type { Template } from './types';

const ajv = new Ajv({ allErrors: true, strict: false });
addFormats(ajv);

// Template schema (simplified for client-side)
const templateSchema = {
  type: 'object',
  required: ['id', 'size', 'bg', 'layers', 'export'],
  properties: {
    id: { type: 'string', pattern: '^[a-z0-9_]+$' },
    size: {
      type: 'object',
      required: ['w', 'h'],
      properties: {
        w: { type: 'integer', minimum: 720, maximum: 2160 },
        h: { type: 'integer', minimum: 720, maximum: 4096 }
      }
    },
    bg: { type: 'object' },
    layers: { type: 'array' },
    export: { type: 'object' }
  }
};

const validateTemplate = ajv.compile<Template>(templateSchema);

export function isValidTemplate(data: unknown): data is Template {
  return validateTemplate(data);
}

export function getTemplateValidationErrors() {
  return validateTemplate.errors?.map(err => ({
    path: err.instancePath,
    message: err.message || 'Validation error'
  })) || [];
}

// Image validation
export interface ImageValidationResult {
  valid: boolean;
  errors: string[];
  width?: number;
  height?: number;
  size?: number;
}

export async function validateImage(
  file: File,
  type: 'thumb' | 'background'
): Promise<ImageValidationResult> {
  const errors: string[] = [];

  // Check MIME type
  if (file.type !== 'image/webp') {
    errors.push('Image must be WEBP format');
  }

  // Check file size
  const maxSize = type === 'thumb' ? 120 * 1024 : 450 * 1024; // 120KB or 450KB
  if (file.size > maxSize) {
    errors.push(`File size ${(file.size / 1024).toFixed(1)}KB exceeds limit of ${maxSize / 1024}KB`);
  }

  // Check dimensions
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;

    if (type === 'thumb') {
      if (width < 480 || width > 720 || height < 480 || height > 720) {
        errors.push(`Thumbnail dimensions ${width}x${height} should be 480-720px`);
      }
    } else if (type === 'background') {
      // Accept square posts (1080x1080) for social media
      if (width !== 1080 || height !== 1080) {
        errors.push(`Background dimensions ${width}x${height} should be 1080x1080`);
      }
    }

    bitmap.close();

    return {
      valid: errors.length === 0,
      errors,
      width,
      height,
      size: file.size
    };
  } catch (err) {
    errors.push('Failed to read image dimensions');
    return { valid: false, errors };
  }
}

// Font validation
export async function validateFont(file: File): Promise<ImageValidationResult> {
  const errors: string[] = [];

  // Check file type
  const validTypes = ['font/ttf', 'font/woff2', 'application/x-font-ttf'];
  if (!validTypes.includes(file.type) && !file.name.match(/\.(ttf|woff2)$/i)) {
    errors.push('Font must be TTF or WOFF2 format');
  }

  // Check file size
  const maxSize = 250 * 1024; // 250KB
  if (file.size > maxSize) {
    errors.push(`Font size ${(file.size / 1024).toFixed(1)}KB exceeds limit of 250KB`);
  }

  return {
    valid: errors.length === 0,
    errors,
    size: file.size
  };
}

// JSON validation
export async function validateJSON<T>(
  file: File,
  validator: (data: unknown) => boolean
): Promise<{ valid: boolean; data?: T; errors: string[] }> {
  try {
    const text = await file.text();
    const data = JSON.parse(text);

    if (!validator(data)) {
      return {
        valid: false,
        errors: ['JSON does not match schema']
      };
    }

    return {
      valid: true,
      data: data as T,
      errors: []
    };
  } catch (err) {
    return {
      valid: false,
      errors: [`Invalid JSON: ${err instanceof Error ? err.message : 'Unknown error'}`]
    };
  }
}
